/**
 * IXVerify v2 — Accuracy dashboard (ixv2 WP-5.2; plan §4 screens inventory:
 * "Accuracy dashboard (admin)", §5.5 metrics of record).
 *
 * Admin-only, READ-ONLY window onto the site's extraction quality + the R11
 * stopwatch. Every number comes from the gated `GET /api/ixverify/metrics`
 * route (server-side aggregation, bounded scans). PHI-free by construction
 * (R3): scores, counts, percentiles, and costs only — never a DOB, subject
 * number, kit value, or document text.
 *
 * The §5.5 metrics of record, made visible (not assumed):
 *   - THE STOPWATCH (R11): start→published p50/p90, LIVE and STAGED separated,
 *     plus the attest→send hand-off latency;
 *   - per-study self-eval accuracy + high-scrutiny state + correction trends;
 *   - mismatch catches, capture-retake rate, adoption, cost per verification.
 *
 * Charts are self-contained (CSS bars + a tiny inline-SVG sparkline) — no chart
 * library is in the bundle, and none is added. Dense laptop-first, dark mode
 * throughout, mobile-usable (R7).
 */
import { useCallback, useEffect, useState } from 'react';
import {
  Gauge, ShieldAlert, Inbox, Timer, TrendingUp, AlertTriangle, DollarSign, Users, Camera, CheckCircle2,
} from 'lucide-react';
import {
  PageHeader,
  RefreshButton,
  EmptyState,
  SegmentedControl,
  useToast,
} from '../../ui';
import { useAuth } from '../../contexts/AuthContext';
import { getMetrics, errorMessage, isAdminRequired, IxvMetrics, IxvStudyMetric, DurationSummary } from '../lib/api';

const WINDOWS = [
  { value: '30', label: '30d' },
  { value: '90', label: '90d' },
  { value: '180', label: '180d' },
];

// ---------------------------------------------------------------------------
// Formatting
// ---------------------------------------------------------------------------

function fmtPct(v: number | null | undefined): string {
  if (v == null || Number.isNaN(v)) return '—';
  return `${Math.round(v * 100)}%`;
}

function fmtMs(ms: number | null | undefined): string {
  if (ms == null || Number.isNaN(ms)) return '—';
  if (ms < 1000) return `${Math.round(ms)}ms`;
  const s = ms / 1000;
  if (s < 60) return `${s.toFixed(s < 10 ? 1 : 0)}s`;
  const m = Math.floor(s / 60);
  const rem = Math.round(s % 60);
  return `${m}m ${String(rem).padStart(2, '0')}s`;
}

function fmtCents(cents: number | null | undefined): string {
  if (cents == null || Number.isNaN(cents)) return '—';
  if (cents < 100) return `${cents.toFixed(cents < 10 ? 2 : 1)}¢`;
  return `$${(cents / 100).toFixed(2)}`;
}

function fmtDateTime(value: string | null | undefined): string {
  if (!value) return 'never';
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleDateString();
}

/** Accuracy → health color family (green healthy / amber watch / red drift). */
function accuracyTone(v: number | null | undefined): 'green' | 'amber' | 'red' | 'gray' {
  if (v == null) return 'gray';
  if (v >= 0.92) return 'green';
  if (v >= 0.85) return 'amber';
  return 'red';
}
/** Correction rate → tone (lower is better; inverse of accuracy). */
function correctionTone(v: number | null | undefined): 'green' | 'amber' | 'red' | 'gray' {
  if (v == null) return 'gray';
  if (v < 0.15) return 'green';
  if (v < 0.30) return 'amber';
  return 'red';
}

const BAR_FILL: Record<string, string> = {
  green: 'bg-green-500 dark:bg-green-500',
  amber: 'bg-amber-500 dark:bg-amber-500',
  red: 'bg-red-500 dark:bg-red-500',
  gray: 'bg-gray-300 dark:bg-gray-600',
  primary: 'bg-primary-500 dark:bg-primary-500',
};
const TEXT_TONE: Record<string, string> = {
  green: 'text-green-700 dark:text-green-400',
  amber: 'text-amber-700 dark:text-amber-400',
  red: 'text-red-700 dark:text-red-400',
  gray: 'text-gray-500 dark:text-gray-400',
};

// ---------------------------------------------------------------------------
// Small self-contained chart primitives
// ---------------------------------------------------------------------------

/** A horizontal proportion bar (0..1), color-coded, dark-mode aware. */
function Bar({ value, tone = 'primary', ariaLabel }: { value: number | null; tone?: string; ariaLabel?: string }) {
  const pct = value == null ? 0 : Math.max(0, Math.min(1, value)) * 100;
  return (
    <div
      className="h-2 w-full overflow-hidden rounded-full bg-gray-100 dark:bg-gray-700"
      role="img"
      aria-label={ariaLabel}
    >
      <div className={`h-full rounded-full ${BAR_FILL[tone] || BAR_FILL.primary}`} style={{ width: `${pct}%` }} />
    </div>
  );
}

/** A tiny inline-SVG accuracy sparkline over the self-eval history. */
function Sparkline({ points }: { points: number[] }) {
  if (points.length < 2) return null;
  const w = 72;
  const h = 20;
  const max = 1;
  const min = Math.min(0.4, ...points);
  const range = max - min || 1;
  const step = w / (points.length - 1);
  const path = points
    .map((p, i) => `${i === 0 ? 'M' : 'L'}${(i * step).toFixed(1)},${(h - ((p - min) / range) * h).toFixed(1)}`)
    .join(' ');
  const last = points[points.length - 1];
  const tone = accuracyTone(last);
  const stroke = tone === 'green' ? '#22c55e' : tone === 'amber' ? '#f59e0b' : tone === 'red' ? '#ef4444' : '#9ca3af';
  return (
    <svg width={w} height={h} viewBox={`0 0 ${w} ${h}`} className="overflow-visible" aria-hidden="true">
      <path d={path} fill="none" stroke={stroke} strokeWidth="1.5" strokeLinejoin="round" strokeLinecap="round" />
    </svg>
  );
}

/** A KPI tile. */
function Tile({
  icon: Icon, label, value, tone = 'gray', hint,
}: { icon: any; label: string; value: string; tone?: string; hint?: string }) {
  return (
    <div className="rounded-xl border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-800">
      <div className="flex items-center gap-2 text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
        <Icon className="h-4 w-4" aria-hidden="true" />
        {label}
      </div>
      <div className={`mt-1.5 text-2xl font-bold tabular-nums ${TEXT_TONE[tone] || 'text-gray-900 dark:text-gray-100'}`}>
        {value}
      </div>
      {hint && <div className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">{hint}</div>}
    </div>
  );
}

/** A stopwatch duration card (p50 + p90 with a bar showing the p90 headroom). */
function DurationCard({ title, subtitle, data }: { title: string; subtitle: string; data: DurationSummary }) {
  const has = data.n > 0 && data.p50_ms != null;
  // p50 filled against p90 as the visual max (so the bar reads "median vs tail").
  const ratio = has && data.p90_ms ? Math.min(1, (data.p50_ms || 0) / data.p90_ms) : 0;
  return (
    <div className="rounded-xl border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-800">
      <div className="flex items-baseline justify-between gap-2">
        <div>
          <div className="flex items-center gap-1.5 text-sm font-semibold text-gray-900 dark:text-gray-100">
            <Timer className="h-4 w-4 text-primary-600 dark:text-primary-400" aria-hidden="true" />
            {title}
          </div>
          <div className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">{subtitle}</div>
        </div>
        <span className="text-xs tabular-nums text-gray-400 dark:text-gray-500">n={data.n}</span>
      </div>
      {has ? (
        <>
          <div className="mt-3 flex items-end gap-4">
            <div>
              <div className="text-[10px] font-medium uppercase tracking-widest text-gray-400 dark:text-gray-500">p50</div>
              <div className="text-xl font-bold tabular-nums text-gray-900 dark:text-gray-100">{fmtMs(data.p50_ms)}</div>
            </div>
            <div>
              <div className="text-[10px] font-medium uppercase tracking-widest text-gray-400 dark:text-gray-500">p90</div>
              <div className="text-xl font-bold tabular-nums text-gray-700 dark:text-gray-300">{fmtMs(data.p90_ms)}</div>
            </div>
          </div>
          <div className="mt-2">
            <Bar value={ratio} tone="primary" ariaLabel={`median ${fmtMs(data.p50_ms)} of tail ${fmtMs(data.p90_ms)}`} />
          </div>
        </>
      ) : (
        <div className="mt-4 text-sm text-gray-400 dark:text-gray-500">No completed sessions in this window.</div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export default function Accuracy() {
  const { toast } = useToast();
  const { isAdmin, isSuperAdmin } = useAuth();
  const canView = isAdmin || isSuperAdmin;

  const [metrics, setMetrics] = useState<IxvMetrics | null>(null);
  const [loading, setLoading] = useState(true);
  const [denied, setDenied] = useState(!canView);
  const [days, setDays] = useState('90');

  const fetchMetrics = useCallback(async () => {
    if (!canView) return;
    setLoading(true);
    try {
      const m = await getMetrics({ days: Number(days) });
      setMetrics(m);
      setDenied(false);
    } catch (err) {
      if (isAdminRequired(err)) setDenied(true);
      else toast(errorMessage(err, 'Failed to load accuracy metrics'), { type: 'error' });
    } finally {
      setLoading(false);
    }
  }, [canView, days, toast]);

  useEffect(() => { fetchMetrics(); }, [fetchMetrics]);

  if (denied) {
    return (
      <div className="space-y-4">
        <PageHeader icon={Gauge} title="Accuracy" />
        <EmptyState
          icon={ShieldAlert}
          title="Administrator access required"
          hint="The accuracy dashboard is visible to site administrators only."
          variant="card"
        />
      </div>
    );
  }

  const s = metrics?.summary;
  const cost = s?.cost;

  // Site-wide accuracy = the lowest per-study self-eval accuracy that exists
  // (the study most at risk); falls back to null when nothing is evaluated yet.
  const evaluated = (metrics?.studies || []).filter((st) => st.self_eval_accuracy != null);
  const worstAccuracy = evaluated.length > 0
    ? Math.min(...evaluated.map((st) => st.self_eval_accuracy as number))
    : null;

  return (
    <div className="space-y-5">
      <PageHeader
        icon={Gauge}
        title="Accuracy"
        subtitle="Extraction quality, drift, and the 30-second stopwatch — per study, this site"
        actions={
          <div className="flex items-center gap-2">
            <SegmentedControl options={WINDOWS} value={days} onChange={setDays} label="Time window" />
            <RefreshButton onClick={fetchMetrics} loading={loading} />
          </div>
        }
      />

      {metrics?.high_scrutiny_studies ? (
        <div className="flex items-start gap-2 rounded-xl border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800 dark:border-amber-800 dark:bg-amber-900/20 dark:text-amber-300">
          <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0" aria-hidden="true" />
          <span>
            <strong className="font-semibold">{metrics.high_scrutiny_studies}</strong>{' '}
            {metrics.high_scrutiny_studies === 1 ? 'study is' : 'studies are'} in high-scrutiny mode — every field is
            mandatory-review until self-eval accuracy recovers. Review the flagged studies below.
          </span>
        </div>
      ) : null}

      {/* KPI tiles */}
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 xl:grid-cols-6">
        <Tile
          icon={CheckCircle2}
          label="Verifications"
          value={s ? s.verifications.toLocaleString() : '—'}
          hint={s ? `${s.adoption.verifications_per_week}/wk` : undefined}
        />
        <Tile
          icon={TrendingUp}
          label="Lowest accuracy"
          value={fmtPct(worstAccuracy)}
          tone={accuracyTone(worstAccuracy)}
          hint={evaluated.length > 0 ? `${evaluated.length} study self-evals` : 'not yet evaluated'}
        />
        <Tile
          icon={AlertTriangle}
          label="Mismatch catches"
          value={s ? s.mismatch_sessions.toLocaleString() : '—'}
          tone={s && s.mismatch_sessions > 0 ? 'amber' : 'gray'}
        />
        <Tile
          icon={Camera}
          label="Capture retakes"
          value={fmtPct(s?.capture_retake_rate)}
          tone={correctionTone(s?.capture_retake_rate)}
          hint={s ? `${s.capture_samples} sampled` : undefined}
        />
        <Tile
          icon={Users}
          label="Adoption"
          value={s ? String(s.adoption.sessions_per_user_per_week) : '—'}
          hint={s ? `${s.adoption.active_users} users · sessions/wk` : undefined}
        />
        <Tile
          icon={DollarSign}
          label="Cost / verification"
          value={fmtCents(cost?.per_verification_cents)}
          hint={cost ? `self-eval ${fmtCents(cost.self_eval_cents)}` : undefined}
        />
      </div>

      {/* The R11 stopwatch */}
      <section>
        <h2 className="mb-2 flex items-center gap-1.5 text-sm font-semibold text-gray-900 dark:text-gray-100">
          <Timer className="h-4 w-4 text-primary-600 dark:text-primary-400" aria-hidden="true" />
          The stopwatch <span className="font-normal text-gray-500 dark:text-gray-400">— start → published (R11 SLA)</span>
        </h2>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
          <DurationCard title="Live" subtitle="At-cabinet start → published" data={metrics?.stopwatch.live ?? { n: 0, p50_ms: null, p90_ms: null }} />
          <DurationCard title="Staged" subtitle="First scan → published (pre-staged)" data={metrics?.stopwatch.staged ?? { n: 0, p50_ms: null, p90_ms: null }} />
          <DurationCard title="Attest → send" subtitle="Sign-off → report hand-off" data={metrics?.stopwatch.attest_to_publish ?? { n: 0, p50_ms: null, p90_ms: null }} />
        </div>
      </section>

      {/* Per-study accuracy */}
      <section>
        <h2 className="mb-2 flex items-center gap-1.5 text-sm font-semibold text-gray-900 dark:text-gray-100">
          <Gauge className="h-4 w-4 text-primary-600 dark:text-primary-400" aria-hidden="true" />
          Per-study accuracy <span className="font-normal text-gray-500 dark:text-gray-400">— flagged studies first</span>
        </h2>
        {!loading && (metrics?.studies || []).length === 0 ? (
          <EmptyState
            icon={Inbox}
            title="No study profiles yet"
            hint="Once studies have confirmed dispenses, the weekly self-eval scores extraction accuracy here."
            variant="card"
          />
        ) : (
          <div className="overflow-hidden rounded-xl border border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-800">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 dark:bg-gray-800/50">
                  <tr>
                    {['Study', 'Self-eval accuracy', 'Trend', 'Correction rate', 'Verifications', 'Last self-eval'].map((h) => (
                      <th key={h} className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                  {(metrics?.studies || []).map((st) => (
                    <StudyRow key={st.site_study_id} st={st} />
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </section>

      {/* Correction trends per field */}
      {metrics && Object.keys(metrics.correction_trends.by_field).length > 0 && (
        <section>
          <h2 className="mb-2 flex items-center gap-1.5 text-sm font-semibold text-gray-900 dark:text-gray-100">
            <TrendingUp className="h-4 w-4 text-primary-600 dark:text-primary-400" aria-hidden="true" />
            Correction trends <span className="font-normal text-gray-500 dark:text-gray-400">— human edits per field, site-wide</span>
          </h2>
          <div className="rounded-xl border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-800">
            <div className="space-y-3">
              {Object.entries(metrics.correction_trends.by_field).map(([fieldName, rate]) => (
                <div key={fieldName} className="flex items-center gap-3">
                  <div className="w-32 flex-shrink-0 text-sm text-gray-700 dark:text-gray-300">{fieldName}</div>
                  <div className="flex-1"><Bar value={rate} tone={correctionTone(rate)} ariaLabel={`${fieldName} correction rate ${fmtPct(rate)}`} /></div>
                  <div className={`w-12 flex-shrink-0 text-right text-sm font-medium tabular-nums ${TEXT_TONE[correctionTone(rate)]}`}>{fmtPct(rate)}</div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {metrics?.truncated && (
        <p className="text-xs text-gray-400 dark:text-gray-500">
          Showing a bounded sample of this window — narrow the window for exact totals.
        </p>
      )}
    </div>
  );
}

/** One per-study row (extracted so the accuracy bar + sparkline stay tidy). */
function StudyRow({ st }: { st: IxvStudyMetric }) {
  const tone = accuracyTone(st.self_eval_accuracy);
  const history = (st.self_eval_history || []).map((h) => h.accuracy).filter((a): a is number => a != null);
  return (
    <tr className={`align-top transition-colors duration-150 hover:bg-gray-50 dark:hover:bg-gray-700/50 ${st.high_scrutiny ? 'bg-amber-50/50 dark:bg-amber-900/10' : ''}`}>
      <td className="px-4 py-3">
        <div className="flex items-center gap-2">
          <span className="font-medium text-gray-900 dark:text-gray-100">{st.protocol_id || '—'}</span>
          {st.high_scrutiny && (
            <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-800 dark:bg-amber-900/40 dark:text-amber-300">
              <AlertTriangle className="h-3 w-3" aria-hidden="true" />
              High scrutiny
            </span>
          )}
        </div>
        <div className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">{st.state}</div>
      </td>
      <td className="px-4 py-3">
        {st.self_eval_accuracy == null ? (
          <span className="text-sm text-gray-400 dark:text-gray-500">not evaluated</span>
        ) : (
          <div className="min-w-[9rem]">
            <div className="mb-1 flex items-center justify-between text-sm">
              <span className={`font-semibold tabular-nums ${TEXT_TONE[tone]}`}>{fmtPct(st.self_eval_accuracy)}</span>
              <span className="text-xs text-gray-400 dark:text-gray-500">n={st.self_eval_sampled}</span>
            </div>
            <Bar value={st.self_eval_accuracy} tone={tone} ariaLabel={`${st.protocol_id || 'study'} accuracy ${fmtPct(st.self_eval_accuracy)}`} />
          </div>
        )}
      </td>
      <td className="px-4 py-3">
        {history.length >= 2 ? <Sparkline points={history} /> : <span className="text-xs text-gray-400 dark:text-gray-500">—</span>}
      </td>
      <td className="px-4 py-3">
        <span className={`text-sm font-medium tabular-nums ${TEXT_TONE[correctionTone(st.correction_rate)]}`}>
          {fmtPct(st.correction_rate)}
        </span>
      </td>
      <td className="px-4 py-3 text-sm tabular-nums text-gray-700 dark:text-gray-300">{st.completed_sessions.toLocaleString()}</td>
      <td className="px-4 py-3 text-sm tabular-nums text-gray-500 dark:text-gray-400">{fmtDateTime(st.self_eval_last_run_at)}</td>
    </tr>
  );
}
